/**
 * services/auth.js
 * Azure Static Web Apps authentication helpers.
 *
 * SWA built-in auth handles login. The application backend resolves the
 * effective app role from existing Entra group membership and exposes it at
 * /api/me. This keeps authorization tied to ReArch's prescribed AD groups
 * instead of SWA invitation/custom-role records.
 *
 * Defined roles:
 *   admin             - designated admins; full access including /admin
 *   leadership        - executive leadership; all-project review access
 *   project_executive - project executive; all non-excluded project review access
 *   pm                - Project Managers/Supers; broad form submission access
 *   reader            - authenticated read-only users
 */

/**
 * @typedef {object} SwaUser
 * @property {string} userId      Entra object ID
 * @property {string} userDetails User principal name / email
 * @property {string[]} userRoles Application roles derived from Entra groups
 * @property {string} [name]      Display name from claims (may be absent)
 * @property {string} email       Normalized email (= userDetails for Entra)
 * @property {string} role        Backend-resolved app role
 * @property {string} roleSource  How the backend resolved the role
 * @property {boolean} roleSwitcherEnabled Whether dev/test role switching is enabled
 * @property {string[]} availableTestRoles Roles available in the dev/test switcher
 * @property {boolean} userSwitcherEnabled Whether dev/test user switching is enabled
 * @property {{email: string, name: string, label: string}[]} availableTestUsers Users available in the dev/test switcher
 * @property {string} selectedTestUser Selected test user email
 */

const TEST_ROLE_STORAGE_KEY = 'execReporting.testRole'
const TEST_USER_STORAGE_KEY = 'execReporting.testUserEmail'
const USER_EMAIL_STORAGE_KEY = 'execReporting.userEmail'
const GRAPH_SCOPES = ['User.Read', 'GroupMember.Read.All']
const GRAPH_INTERACTION_STORAGE_KEY = 'execReporting.graphInteractionStartedAt'
const GRAPH_INTERACTION_STALE_MS = 120000

let msalInstance = null
let graphTokenPromise = null
let currentGraphConfig = null
let graphInteractiveAttempted = false

export const TEST_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'project_executive', label: 'Project Executive' },
  { value: 'pm', label: 'PM' },
  { value: 'reader', label: 'Reader' },
]

export function getTestRoleOverride() {
  if (typeof window === 'undefined' || !window.localStorage) return ''
  return window.localStorage.getItem(TEST_ROLE_STORAGE_KEY) || ''
}

export function setTestRoleOverride(role) {
  if (typeof window === 'undefined' || !window.localStorage) return
  if (role) {
    window.localStorage.setItem(TEST_ROLE_STORAGE_KEY, role)
  } else {
    window.localStorage.removeItem(TEST_ROLE_STORAGE_KEY)
  }
}

export function getTestUserOverride() {
  if (typeof window === 'undefined' || !window.localStorage) return ''
  return window.localStorage.getItem(TEST_USER_STORAGE_KEY) || ''
}

export function setTestUserOverride(email) {
  if (typeof window === 'undefined' || !window.localStorage) return
  if (email) {
    window.localStorage.setItem(TEST_USER_STORAGE_KEY, email)
  } else {
    window.localStorage.removeItem(TEST_USER_STORAGE_KEY)
  }
}

function getStoredUserEmail() {
  if (typeof window === 'undefined' || !window.localStorage) return ''
  return window.localStorage.getItem(USER_EMAIL_STORAGE_KEY) || ''
}

function setStoredUserEmail(email) {
  if (typeof window === 'undefined' || !window.localStorage) return
  if (email) {
    window.localStorage.setItem(USER_EMAIL_STORAGE_KEY, email)
  } else {
    window.localStorage.removeItem(USER_EMAIL_STORAGE_KEY)
  }
}

function claimValue(principal, types) {
  return (principal?.claims || []).find((claim) => types.includes(claim.typ))?.val || ''
}

function getSwaAuthConfig(principal) {
  const clientId = claimValue(principal, ['aud'])
  const tenantClaim = claimValue(principal, ['http://schemas.microsoft.com/identity/claims/tenantid', 'tid'])
  const issuer = claimValue(principal, ['iss'])
  const tenantFromIssuer = issuer.match(/login\.microsoftonline\.com\/([^/]+)/)?.[1] || ''
  const tenantId = tenantClaim || tenantFromIssuer
  return clientId && tenantId ? { clientId, tenantId } : null
}

async function fetchSwaAuthContext() {
  try {
    const res = await fetch('/.auth/me', { credentials: 'include' })
    if (!res.ok) return { email: '', graphConfig: null }
    const body = await res.json()
    const principal = body?.clientPrincipal
    const claimEmail = claimValue(principal, [
      'preferred_username',
      'email',
      'emails',
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
    ])
    const userDetails = principal?.userDetails || ''
    const graphConfig = getSwaAuthConfig(principal)
    if (graphConfig) currentGraphConfig = graphConfig
    return {
      email: claimEmail || (String(userDetails).includes('@') ? userDetails : '') || '',
      graphConfig,
    }
  } catch {
    return { email: '', graphConfig: null }
  }
}

function getMsalRedirectUri() {
  return `${window.location.origin}/`
}

async function getMsalInstance(graphConfig) {
  if (!graphConfig?.clientId || !graphConfig?.tenantId) return null
  if (msalInstance) return msalInstance
  const { PublicClientApplication } = await import('@azure/msal-browser')
  msalInstance = new PublicClientApplication({
    auth: {
      clientId: graphConfig.clientId,
      authority: `https://login.microsoftonline.com/${graphConfig.tenantId}`,
      redirectUri: getMsalRedirectUri(),
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    }
  })
  await msalInstance.initialize()
  return msalInstance
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function getGraphInteractionStartedAt() {
  if (typeof window === 'undefined' || !window.sessionStorage) return 0
  const startedAt = Number(window.sessionStorage.getItem(GRAPH_INTERACTION_STORAGE_KEY) || 0)
  if (startedAt && Date.now() - startedAt > GRAPH_INTERACTION_STALE_MS) {
    window.sessionStorage.removeItem(GRAPH_INTERACTION_STORAGE_KEY)
    return 0
  }
  return startedAt
}

function setGraphInteractionStarted() {
  if (typeof window === 'undefined' || !window.sessionStorage) return
  window.sessionStorage.setItem(GRAPH_INTERACTION_STORAGE_KEY, String(Date.now()))
}

function clearGraphInteractionStarted() {
  if (typeof window === 'undefined' || !window.sessionStorage) return
  window.sessionStorage.removeItem(GRAPH_INTERACTION_STORAGE_KEY)
}

function isInteractionInProgressError(err) {
  return String(err?.errorCode || err?.message || '').includes('interaction_in_progress')
}

async function trySilentGraphToken(msal) {
  const account = msal.getAllAccounts()[0]
  if (!account) return ''
  const response = await msal.acquireTokenSilent({ scopes: GRAPH_SCOPES, account })
  return response.accessToken || ''
}

async function acquireGraphToken(graphConfig, loginHint, options = {}) {
  if (!graphConfig) return ''
  if (graphTokenPromise) return graphTokenPromise
  if (options.forceInteractive) graphInteractiveAttempted = false

  graphTokenPromise = (async () => {
    try {
      const msal = await getMsalInstance(graphConfig)
      if (!msal) return ''

      const silentToken = await trySilentGraphToken(msal)
      if (silentToken) return silentToken

      if (!options.allowInteractive || graphInteractiveAttempted) return ''
      if (getGraphInteractionStartedAt()) {
        await sleep(1000)
        return await trySilentGraphToken(msal)
      }

      graphInteractiveAttempted = true
      setGraphInteractionStarted()
      const popupResponse = await msal.acquireTokenPopup({
        scopes: GRAPH_SCOPES,
        loginHint,
        redirectUri: getMsalRedirectUri(),
      })
      return popupResponse.accessToken || ''
    } catch (err) {
      if (!isInteractionInProgressError(err)) {
        console.warn('[auth] Graph token acquisition failed:', err)
      }
      return ''
    } finally {
      clearGraphInteractionStarted()
      graphTokenPromise = null
    }
  })()

  return graphTokenPromise
}

export async function getAuthContextHeaders(graphConfig = null, loginHint = '', options = {}) {
  const headers = {}
  const role = getTestRoleOverride()
  const testUserEmail = getTestUserOverride()
  const email = getStoredUserEmail()
  if (role) headers['x-exec-reporting-test-role'] = role
  if (testUserEmail) headers['x-exec-reporting-test-user-email'] = testUserEmail
  if (email) headers['x-user-email'] = email
  const graphToken = await acquireGraphToken(graphConfig || currentGraphConfig, loginHint || email, options)
  if (graphToken) headers['x-graph-token'] = graphToken
  return headers
}

export function getTestRoleHeaders() {
  const role = getTestRoleOverride()
  const testUserEmail = getTestUserOverride()
  const email = getStoredUserEmail()
  return {
    ...(role ? { 'x-exec-reporting-test-role': role } : {}),
    ...(testUserEmail ? { 'x-exec-reporting-test-user-email': testUserEmail } : {}),
    ...(email ? { 'x-user-email': email } : {}),
  }
}

async function fetchApiUser(headers = {}) {
  const res = await fetch('/api/me', {
    credentials: 'include',
    headers,
  })

  if (!res.ok) return null

  const body = await res.json()
  return body?.data || null
}

function normalizeCurrentUser(currentUser, fallbackEmail = '') {
  if (!currentUser) return null

  return {
    userId: currentUser.id,
    email: currentUser.email || fallbackEmail,
    userDetails: currentUser.email || fallbackEmail,
    userRoles: [currentUser.role],
    role: currentUser.role,
    roleSource: currentUser.roleSource,
    roleSwitcherEnabled: Boolean(currentUser.roleSwitcherEnabled),
    availableTestRoles: currentUser.availableTestRoles || [],
    userSwitcherEnabled: Boolean(currentUser.userSwitcherEnabled),
    availableTestUsers: currentUser.availableTestUsers || [],
    selectedTestUser: currentUser.selectedTestUser || '',
    name: currentUser.name || currentUser.email || fallbackEmail,
  }
}

/**
 * Fetch the current authenticated user from SWA's built-in auth endpoint.
 * Returns null if the user is not authenticated.
 *
 * @returns {Promise<SwaUser|null>}
 */
export async function fetchCurrentUser(options = {}) {
  try {
    if (options.forceInteractive && currentGraphConfig) {
      const storedEmail = getStoredUserEmail()
      const headers = await getAuthContextHeaders(currentGraphConfig, storedEmail, {
        allowInteractive: true,
        forceInteractive: true,
      })
      return normalizeCurrentUser(await fetchApiUser(headers), storedEmail)
    }

    const { email: swaEmail, graphConfig } = await fetchSwaAuthContext()
    if (swaEmail) setStoredUserEmail(swaEmail)

    const headers = await getAuthContextHeaders(graphConfig, swaEmail, options)
    return normalizeCurrentUser(await fetchApiUser(headers), swaEmail)
  } catch (err) {
    console.warn('[auth] fetchCurrentUser failed:', err)
    return null
  }
}

// ---- Role helpers ----

/**
 * True if the user has the 'admin' role.
 *
 * @param {SwaUser|null} user
 * @returns {boolean}
 */
export function hasAdminRole(user) {
  return Array.isArray(user?.userRoles) && user.userRoles.includes('admin')
}

/**
 * True if the user has a review role (or admin, which implies review access).
 *
 * @param {SwaUser|null} user
 * @returns {boolean}
 */
export function hasExecRole(user) {
  if (!user?.userRoles) return false
  return user.userRoles.includes('leadership')
    || user.userRoles.includes('project_executive')
    || user.userRoles.includes('executive')
    || user.userRoles.includes('exec')
    || user.userRoles.includes('admin')
}

/**
 * True if the user has the 'pm' role (or admin).
 *
 * @param {SwaUser|null} user
 * @returns {boolean}
 */
export function hasPmRole(user) {
  if (!user?.userRoles) return false
  return user.userRoles.includes('pm') || user.userRoles.includes('admin')
}

/**
 * True if the user is authenticated at all (has any non-anonymous role).
 *
 * @param {SwaUser|null} user
 * @returns {boolean}
 */
export function isAuthenticated(user) {
  if (!user?.userRoles) return false
  return user.userRoles.some((r) => r !== 'anonymous' && r !== 'reader') || user.userRoles.includes('reader')
}

/**
 * Returns the SWA login URL for Microsoft/Entra ID.
 *
 * @param {string} [returnPath] Path to redirect to after login
 * @returns {string}
 */
export function getLoginUrl(returnPath = '/dashboard') {
  const encoded = encodeURIComponent(returnPath)
  return `/.auth/login/aad?post_login_redirect_uri=${encoded}`
}

/**
 * Returns the SWA logout URL.
 *
 * @param {string} [returnPath] Path to redirect to after logout
 * @returns {string}
 */
export function getLogoutUrl(returnPath = '/login') {
  const encoded = encodeURIComponent(returnPath)
  return `/.auth/logout?post_logout_redirect_uri=${encoded}`
}
